import type {
  Subscription,
  SubscriptionDetailModule,
  SubscriptionDetails,
  SubscriptionModule,
  SubscriptionModuleInput,
  SubscriptionModuleRef,
  SubscriptionModuleStatus,
  SubscriptionStatus,
  SubscriptionWriteInput,
} from "../domain";
import type {
  SubscriptionDetailModuleLineDto,
  SubscriptionDetailsDto,
  SubscriptionDto,
  SubscriptionModuleLineDto,
  SubscriptionModuleRefDto,
  SubscriptionModuleWriteDto,
  SubscriptionWriteDto,
} from "./subscriptionDtos";

/**
 * Mirrors the backend's own derivation rule ("ACTIVE if any line live, else
 * highest-priority of SCHEDULED/EXPIRED/INACTIVE") so a create/addModules
 * response — which carries no top-level status — still gets a correct one
 * instead of a guess.
 */
export function deriveSubscriptionStatus(
  modules: readonly { readonly status: SubscriptionModuleStatus }[],
): SubscriptionStatus {
  if (modules.some((module) => module.status === "ACTIVE")) return "ACTIVE";
  if (modules.some((module) => module.status === "SCHEDULED"))
    return "SCHEDULED";
  if (modules.some((module) => module.status === "EXPIRED")) return "EXPIRED";
  return "INACTIVE";
}

function totalPrice(modules: readonly { readonly purchasingPrice: number }[]) {
  return modules.reduce((sum, module) => sum + module.purchasingPrice, 0);
}

export function moduleRefFromDto(
  dto: SubscriptionModuleRefDto,
): SubscriptionModuleRef {
  return { ...dto };
}

export function subscriptionModuleFromDto(
  dto: SubscriptionModuleLineDto,
): SubscriptionModule {
  return { ...dto, module: moduleRefFromDto(dto.module) };
}

export function subscriptionDetailModuleFromDto(
  dto: SubscriptionDetailModuleLineDto,
): SubscriptionDetailModule {
  return { ...dto, module: moduleRefFromDto(dto.module) };
}

export function subscriptionFromDto(dto: SubscriptionDto): Subscription {
  const modules = dto.modules.map(subscriptionModuleFromDto);
  return {
    id: dto.id,
    subscriberId: dto.subscriberId,
    subscriber: { ...dto.subscriber },
    modules,
    devices: dto.devices.map((device) => ({ ...device })),
    totalSubscriptionPrice: dto.totalSubscriptionPrice ?? totalPrice(modules),
    status: dto.status ?? deriveSubscriptionStatus(modules),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

export function subscriptionDetailsFromDto(
  dto: SubscriptionDetailsDto,
): SubscriptionDetails {
  return {
    id: dto.id,
    subscriber: { ...dto.subscriber },
    status: dto.status,
    totalSubscriptionPrice: dto.totalSubscriptionPrice,
    current: dto.current.map(subscriptionDetailModuleFromDto),
    history: dto.history.map(subscriptionDetailModuleFromDto),
    future: dto.future.map(subscriptionDetailModuleFromDto),
    assignableModules: dto.assignableModules.map(moduleRefFromDto),
    devices: dto.devices.map((device) => ({ ...device })),
  };
}

export function moduleInputToDto(
  input: SubscriptionModuleInput,
): SubscriptionModuleWriteDto {
  return {
    moduleId: input.moduleId,
    ...(input.purchasingPrice === undefined
      ? {}
      : { purchasingPrice: input.purchasingPrice }),
    startDate: input.startDate,
    endDate: input.endDate,
  };
}

export function subscriptionToDto(
  input: SubscriptionWriteInput,
): SubscriptionWriteDto {
  return {
    subscriberId: input.subscriberId,
    modules: input.modules.map(moduleInputToDto),
  };
}
